import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Chip,
  Collapse,
  IconButton,
  TablePagination,
  Alert
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  TrendingUp,
  TrendingDown
} from '@mui/icons-material';
import EncodingInfo from './EncodingInfo';

const TransactionRow = ({ transaction, index }) => {
  const [expanded, setExpanded] = useState(false);

  const formatAmount = (amount, debitCredit) => {
    const numAmount = parseFloat(amount.replace(',', '.'));
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: 'PLN'
    }).format(debitCredit === 'D' ? -numAmount : numAmount);
  };

  const formatDate = (dateStr) => {
    if (dateStr.length !== 6) return dateStr;
    const year = '20' + dateStr.substring(0, 2);
    const month = dateStr.substring(2, 4);
    const day = dateStr.substring(4, 6);
    return `${day}.${month}.${year}`;
  };

  const getOperationTypeColor = (debitCredit) => {
    return debitCredit === 'C' ? 'success' : 'error';
  };

  return (
    <>
      <TableRow hover>
        <TableCell>
          <IconButton
            size="small"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </TableCell>
        <TableCell>{index + 1}</TableCell>
        <TableCell>{formatDate(transaction.valueDate)}</TableCell>
        <TableCell>
          <Box display="flex" alignItems="center" gap={1}>
            {transaction.debitCredit === 'C' ? (
              <TrendingUp color="success" fontSize="small" />
            ) : (
              <TrendingDown color="error" fontSize="small" />
            )}
            <Chip
              label={transaction.debitCredit === 'C' ? 'Uznanie' : 'Obciążenie'}
              size="small"
              color={getOperationTypeColor(transaction.debitCredit)}
              variant="outlined"
            />
          </Box>
        </TableCell>
        <TableCell align="right" sx={{ fontWeight: 'medium' }}>
          {formatAmount(transaction.amount, transaction.debitCredit)}
        </TableCell>
        <TableCell sx={{ maxWidth: 200 }}>
          <Typography variant="body2" noWrap>
            {transaction.additionalInfo.description || 'Brak opisu'}
          </Typography>
        </TableCell>
        <TableCell sx={{ maxWidth: 150 }}>
          <Typography variant="body2" noWrap>
            {transaction.additionalInfo.counterpartyName || '-'}
          </Typography>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={7} sx={{ py: 0 }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <Typography variant="h6" gutterBottom component="div">
                Szczegóły transakcji
              </Typography>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Data waluty
                    </Typography>
                    <Typography variant="body2">
                      {formatDate(transaction.valueDate)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Data księgowania
                    </Typography>
                    <Typography variant="body2">
                      {formatDate(transaction.entryDate)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Typ transakcji
                    </Typography>
                    <Typography variant="body2">
                      {transaction.type || 'Nieznany'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Referencja
                    </Typography>
                    <Typography variant="body2">
                      {transaction.reference || '-'}
                    </Typography>
                  </Box>
                  {transaction.additionalInfo.accountNumber && (
                    <>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Rachunek kontrahenta
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {transaction.additionalInfo.accountNumber}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Kod banku
                        </Typography>
                        <Typography variant="body2">
                          {transaction.additionalInfo.bankCode}
                        </Typography>
                      </Box>
                    </>
                  )}
                  {transaction.additionalInfo.counterpartyAddress && (
                    <Box sx={{ gridColumn: '1 / -1' }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Adres kontrahenta
                      </Typography>
                      <Typography variant="body2">
                        {transaction.additionalInfo.counterpartyAddress}
                      </Typography>
                    </Box>
                  )}
                  {transaction.additionalInfo.remittanceInfo && (
                    <Box sx={{ gridColumn: '1 / -1' }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Tytuł płatności
                      </Typography>
                      <Typography variant="body2">
                        {transaction.additionalInfo.remittanceInfo}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const TransactionPreview = ({ statements, fileContent }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  if (!statements || statements.length === 0) {
    return (
      <Alert severity="info">
        Nie wczytano żadnych danych. Proszę wgrać plik MT940.
      </Alert>
    );
  }

  // Zbierz wszystkie transakcje ze wszystkich wyciągów
  const allTransactions = statements.reduce((acc, statement) => {
    return acc.concat(statement.transactions.map(transaction => ({
      ...transaction,
      statementRef: statement.reference,
      account: statement.account
    })));
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedTransactions = allTransactions.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const totalAmount = allTransactions.reduce((sum, transaction) => {
    const amount = parseFloat(transaction.amount.replace(',', '.'));
    return sum + (transaction.debitCredit === 'C' ? amount : -amount);
  }, 0);

  return (
    <Box>
      {fileContent && <EncodingInfo content={fileContent} fileName="MT940" />}

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          Podgląd transakcji ({allTransactions.length})
        </Typography>
        <Box display="flex" gap={2}>
          <Chip
            label={`Suma: ${new Intl.NumberFormat('pl-PL', {
              style: 'currency',
              currency: 'PLN'
            }).format(totalAmount)}`}
            color={totalAmount >= 0 ? 'success' : 'error'}
            variant="outlined"
          />
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width={50}></TableCell>
              <TableCell width={60}>Lp.</TableCell>
              <TableCell width={120}>Data</TableCell>
              <TableCell width={140}>Typ</TableCell>
              <TableCell width={140} align="right">Kwota</TableCell>
              <TableCell>Opis</TableCell>
              <TableCell>Kontrahent</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedTransactions.length > 0 ? (
              paginatedTransactions.map((transaction, index) => (
                <TransactionRow
                  key={`${transaction.statementRef}-${index}`}
                  transaction={transaction}
                  index={page * rowsPerPage + index}
                />
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary">
                    Brak transakcji do wyświetlenia
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={allTransactions.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Wierszy na stronie:"
        labelDisplayedRows={({ from, to, count }) =>
          `${from}–${to} z ${count !== -1 ? count : `więcej niż ${to}`}`
        }
      />
    </Box>
  );
};

export default TransactionPreview;